<template>
  <div class="campaigns-page">

    <!-- Page header -->
    <div class="cp-header">
      <div>
        <h1 class="cp-title">Campaigns</h1>
        <p class="cp-sub">Create, track and manage your influencer campaigns.</p>
      </div>
    </div>

    <!-- Filters row -->
    <div class="cp-filters">
      <div class="cp-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="cp-tab"
          :class="{ active: activeTab === tab.key }"
          @click="switchTab(tab.key)"
        >
          {{ tab.label }}
          <span class="cp-tab-count" :class="{ 'cp-tab-count--active': activeTab === tab.key }">
            {{ tab.count !== null ? tab.count : '–' }}
          </span>
        </button>
      </div>
      <div class="cp-filter-right">
        <div class="cp-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cp-search-icon"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input v-model="search" type="text" placeholder="Search campaigns…" class="cp-search" />
        </div>

        <!-- Country filter -->
        <div class="cp-filter-btn-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="3 6 10 3 14 15 21 12 21 22 3 22"/></svg>
          <span class="cp-filter-label">Country:</span>
          <select v-model="selectedCountry" class="cp-filter-select">
            <option value="">All countries</option>
            <option v-for="c in countries" :key="c.iso_3166_2" :value="c.iso_3166_2">{{ c.name }}</option>
          </select>
          <span class="cp-filter-value">{{ selectedCountryName }}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <!-- Sort filter -->
        <div class="cp-filter-btn-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="9" y1="18" x2="15" y2="18"/></svg>
          <span class="cp-filter-label">Sort:</span>
          <select v-model="sortBy" class="cp-filter-select">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name A–Z</option>
          </select>
          <span class="cp-filter-value">{{ sortLabel }}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="cp-grid">
      <div v-for="i in 6" :key="i" class="cp-skeleton">
        <div class="cp-skel cp-skel--img"></div>
        <div style="padding:16px">
          <div class="cp-skel cp-skel--line" style="width:40%;margin-bottom:8px"></div>
          <div class="cp-skel cp-skel--line" style="width:80%;margin-bottom:16px"></div>
          <div class="cp-skel cp-skel--line" style="width:60%"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredCampaigns.length === 0" class="cp-empty">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" style="color:var(--ink-4)"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.82a19.79 19.79 0 01-3.07-8.7A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
      <p style="margin:12px 0 4px;font-weight:600;color:var(--ink)">No campaigns yet</p>
      <p style="font-size:13px;color:var(--ink-3);margin:0 0 16px">Create your first campaign to get started</p>
      <button class="btn btn-primary" style="height:36px;padding:0 16px" @click="openCreateDialog()">
        + Create campaign
      </button>
    </div>

    <!-- Campaign grid -->
    <div v-else class="cp-grid">
      <div
        v-for="campaign in filteredCampaigns"
        :key="campaign.uuid"
        class="cp-card"
        @click="openCampaign(campaign)"
      >
        <!-- Cover image -->
        <div class="cp-card-img">
          <img v-if="campaign.image_url" :src="campaign.image_url" :alt="campaign.name" />
          <div v-else class="cp-card-img-placeholder">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" style="color:var(--ink-4)"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.82a19.79 19.79 0 01-3.07-8.7A2 2 0 012 0h3"/></svg>
          </div>
          <div class="cp-card-badges">
            <span class="cp-badge cp-badge--type">{{ campaign.country && campaign.country.iso_3166_2 ? campaign.country.iso_3166_2.toUpperCase() : '' }} · {{ commissionLabel(campaign) }}</span>
          </div>
          <div class="cp-card-title-overlay">{{ campaign.name }}</div>
        </div>

        <!-- Card body -->
        <div class="cp-card-body">
          <!-- Status + category badges -->
          <div class="cp-card-meta">
            <span class="cp-status-badge" :class="statusClass(campaign)">
              <span class="cp-status-dot-sm"></span>
              {{ statusLabel(campaign) }}
            </span>
            <span v-if="campaign.category" class="cp-cat-badge">{{ campaign.category.display_name }}</span>
          </div>

          <!-- Description -->
          <p class="cp-card-desc">{{ stripHtml(campaign.description || campaign.name) }}</p>

          <!-- Info box -->
          <div class="cp-info-box">
            <div v-if="!campaign.published_at" class="cp-info-row">
              <span class="cp-info-label">STATUS</span>
              <span class="cp-info-val">Not published — finish setup</span>
            </div>

            <!-- Stats grid -->
            <div class="cp-stats-grid">
              <div class="cp-stat-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                <div>
                  <span class="cp-stat-label">Participants</span>
                  <span class="cp-stat-val">{{ campaign.participants || 0 }}</span>
                </div>
              </div>
              <div class="cp-stat-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                <div>
                  <span class="cp-stat-label">Follower req.</span>
                  <span class="cp-stat-val">{{ fmtFollowers(campaign.min_followers) }}</span>
                </div>
              </div>
              <div class="cp-stat-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <div>
                  <span class="cp-stat-label">Start</span>
                  <span class="cp-stat-val">{{ fmtDate(campaign.starting_at) }}</span>
                </div>
              </div>
              <div class="cp-stat-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <div>
                  <span class="cp-stat-label">End</span>
                  <span class="cp-stat-val">{{ fmtDate(campaign.ending_at) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress bar (active campaigns only) -->
          <div v-if="campaign.published_at && campaign.starting_at && campaign.ending_at" class="cp-progress-wrap">
            <div class="cp-progress-bar">
              <div class="cp-progress-fill" :style="{ width: progressPct(campaign) + '%' }"></div>
            </div>
            <span class="cp-progress-pct">{{ progressPct(campaign) }}%</span>
          </div>

          <!-- CTA -->
          <div class="cp-cta-row">
            <button class="cp-cta" :class="{ 'cp-cta--draft': !campaign.published_at }" @click.stop="openCampaign(campaign)">
              {{ campaign.published_at ? 'See campaign' : 'Continue setup' }} →
            </button>
            <button v-if="!campaign.published_at" class="cp-cta-delete" @click.stop="deleteDraft(campaign, $event)" title="Delete draft">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            </button>
            <button v-if="campaign.published_at" class="cp-cta-delete" @click.stop="toggleHidden(campaign, $event)" :title="campaign.hidden ? 'Make public' : 'Hide campaign'">
              <svg v-if="campaign.hidden" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="lastPage > 1" class="cp-pagination">
      <button class="cp-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">←</button>
      <span style="font-size:13px;color:var(--ink-2)">Page {{ currentPage }} of {{ lastPage }}</span>
      <button class="cp-page-btn" :disabled="currentPage === lastPage" @click="changePage(currentPage + 1)">→</button>
    </div>

    <!-- Create campaign dialog -->
    <div v-if="createDialog" class="cp-dialog-overlay" @click.self="createDialog = false">
      <div class="cp-dialog">
        <h3 class="cp-dialog-title">New campaign</h3>
        <p class="cp-dialog-sub">Select the country for this campaign</p>
        <select v-model="newCampaignCountry" class="cp-dialog-select">
          <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <div class="cp-dialog-actions">
          <button class="cp-dialog-cancel" @click="createDialog = false">Cancel</button>
          <button class="btn btn-primary" :disabled="isCreating" @click="newCampaign()">
            {{ isCreating ? 'Creating…' : 'Create campaign' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'CampaignsIndex',
  data() {
    return {
      isLoading: true,
      campaigns: [],
      currentPage: 1,
      lastPage: 1,
      activeTab: 'active',
      search: '',
      selectedCountry: '',
      sortBy: 'newest',
      counts: { active: null, completed: null, drafts: null },
      countries: [],
      isCreating: false,
      createDialog: false,
      newCampaignCountry: 61,
    };
  },
  computed: {
    ...mapState('core/auth', ['user']),
    ...mapGetters('core/auth', ['isAgency']),
    tabs() {
      return [
        { key: 'active',    label: 'Active',    count: this.counts.active },
        { key: 'completed', label: 'Completed', count: this.counts.completed },
        { key: 'drafts',    label: 'Drafts',    count: this.counts.drafts },
      ];
    },
    selectedCountryName() {
      const found = this.countries.find(c => c.iso_3166_2 === this.selectedCountry);
      return found ? found.name : (this.selectedCountry || 'All countries');
    },
    sortLabel() {
      return { newest: 'Newest', oldest: 'Oldest', name: 'Name A–Z' }[this.sortBy] || 'Newest';
    },
    filteredCampaigns() {
      let list = this.campaigns;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(c => c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
      }
      if (this.sortBy === 'oldest') list = [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      if (this.sortBy === 'newest') list = [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (this.sortBy === 'name')   list = [...list].sort((a, b) => a.name.localeCompare(b.name));
      return list;
    },
  },
  watch: {
    selectedCountry() { this.currentPage = 1; this.fetchCampaigns(); },
    sortBy()          { this.currentPage = 1; this.fetchCampaigns(); },
  },
  methods: {
    ...mapActions('core', ['getCountries']),
    ...mapActions('core/campaigns', ['loadCampaigns', 'createCampaign', 'removeCampaign', 'updateCampaign']),

    baseParams() {
      const p = {};
      const businessUuid = this.user?.business?.uuid;
      if (businessUuid)        p.business_uuid  = businessUuid;
      if (this.selectedCountry) p.country_codes  = [this.selectedCountry];
      return p;
    },

    fetchParams() {
      const base = { ...this.baseParams(), page: this.currentPage, limit: 15 };
      if (this.activeTab === 'active')    return { ...base, active_campaigns: true,  draft: false };
      if (this.activeTab === 'completed') return { ...base, active_campaigns: false, draft: false };
      if (this.activeTab === 'drafts')    return { ...base, draft: true };
      return base;
    },

    fetchCampaigns() {
      this.isLoading = true;
      this.loadCampaigns(this.fetchParams()).then(
        result => {
          this.campaigns = result.response || [];
          this.lastPage  = result.lastPage  || 1;
          this.counts[this.activeTab] = result.total || 0;
          this.extractCountries(this.campaigns);
          this.isLoading = false;
        },
        err => { console.error('[campaigns] error:', err); this.isLoading = false; }
      );
    },

    toggleHidden(campaign, event) {
      event.stopPropagation();
      const newHidden = !campaign.hidden;
      this.updateCampaign({ uuid: campaign.uuid, hidden: newHidden }).then(() => {
        campaign.hidden = newHidden;
      });
    },

    fetchAllCounts() {
      const base = { ...this.baseParams(), page: 1, limit: 1 };
      Promise.all([
        this.loadCampaigns({ ...base, active_campaigns: true,  draft: false }),
        this.loadCampaigns({ ...base, active_campaigns: false, draft: false }),
        this.loadCampaigns({ ...base, draft: true }),
      ]).then(([active, completed, drafts]) => {
        this.counts.active    = active.total    || 0;
        this.counts.completed = completed.total || 0;
        this.counts.drafts    = drafts.total    || 0;
      });
    },

    stripHtml(str) {
      if (!str) return '';
      return str.replace(/<[^>]*>/g, '').trim();
    },

    switchTab(key) {
      this.activeTab   = key;
      this.currentPage = 1;
      this.fetchCampaigns();
    },

    changePage(p) {
      this.currentPage = p;
      this.fetchCampaigns();
    },

    extractCountries(campaigns) {
      campaigns.forEach(c => {
        if (c.country?.iso_3166_2 && !this.countries.find(x => x.iso_3166_2 === c.country.iso_3166_2)) {
          this.countries.push(c.country);
        }
      });
    },

    openCreateDialog() {
      this.createDialog = true;
    },

    newCampaign() {
      if (this.isCreating) return;
      this.isCreating = true;
      this.createCampaign({
        business_uuid: this.user?.business?.uuid,
        country_id: this.newCampaignCountry,
      }).then(
        created => {
          this.isCreating = false;
          this.createDialog = false;
          this.$router.push({ name: 'campaign.create', params: { id: created.uuid } });
        },
        err => { console.error('[campaigns] create error:', err); this.isCreating = false; }
      );
    },

    openCampaign(campaign) {
      if (campaign.published_at) {
        this.$router.push({ name: 'campaign', params: { id: campaign.uuid } });
      } else {
        this.$router.push({ name: 'campaign.create', params: { id: campaign.uuid } });
      }
    },

    commissionLabel(campaign) {
      if (campaign.cost_per_action_percent) return 'Per sale';
      if (campaign.cost_per_click)          return 'Per click';
      if (campaign.cost_per_action_fixed)   return 'Fixed';
      return 'Per sale';
    },

    statusLabel(campaign) {
      if (!campaign.published_at) return 'Draft';
      const name = campaign.status?.name?.toLowerCase();
      if (name === 'ended') return 'Completed';
      if (campaign.ending_at && this.$moment(campaign.ending_at).isSame(this.$moment(), 'day')) return 'Ends today';
      return 'Active';
    },

    statusClass(campaign) {
      const label = this.statusLabel(campaign);
      if (label === 'Active')     return 'cp-status-dot--active';
      if (label === 'Ends today') return 'cp-status-dot--warning';
      if (label === 'Draft')      return 'cp-status-dot--draft';
      return 'cp-status-dot--ended';
    },

    progressPct(campaign) {
      const start = new Date(campaign.starting_at);
      const end   = new Date(campaign.ending_at);
      const now   = new Date();
      if (now >= end)   return 100;
      if (now <= start) return 0;
      return Math.round(((now - start) / (end - start)) * 100);
    },

    fmtDate(d) {
      if (!d) return '—';
      return this.$moment(d).format('DD MMM YYYY');
    },

    fmtFollowers(n) {
      if (!n) return '—';
      if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'k';
      return String(n);
    },

    deleteDraft(campaign, event) {
      event.stopPropagation();
      if (!confirm(`Delete "${campaign.name || 'this draft'}"?`)) return;
      this.removeCampaign({ uuid: campaign.uuid }).then(
        () => {
          this.campaigns = this.campaigns.filter(c => c.uuid !== campaign.uuid);
          this.counts.drafts = Math.max(0, (this.counts.drafts || 1) - 1);
        },
        err => console.error('[campaigns] delete error:', err)
      );
    },

    loadCountries() {
      this.getCountries(null).then(
        countries => {
          this.countries = countries.filter(c => c.active);
        },
        err => console.error('[campaigns] countries error:', err)
      );
    },
  },

  mounted() {
    this.loadCountries();
    this.fetchCampaigns();
    this.fetchAllCounts();
  },
};
</script>

<style scoped>
.campaigns-page {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 24px 20px 48px;
}

/* Header */
.cp-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.cp-title { font-size: 22px; font-weight: 700; color: var(--ink); margin: 0 0 2px; }
.cp-sub   { font-size: 13px; color: var(--ink-3); margin: 0; }

/* Filters */
.cp-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.cp-tabs {
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: var(--r-ctrl);
  background: #ede8e3;
  padding: 3px;
  gap: 2px;
}
.cp-tab {
  height: 30px;
  padding: 0 12px;
  border-radius: calc(var(--r-ctrl) - 2px);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-3);
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background .14s, color .14s;
}
.cp-tab:hover { color: var(--ink); }
.cp-tab.active {
  background: #fff;
  color: var(--ink);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.cp-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 17px;
  padding: 0 5px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  background: var(--line-2);
  color: var(--ink-3);
  line-height: 1;
}
.cp-tab.active .cp-tab-count {
  background: var(--accent);
  color: #fff;
}
.cp-filter-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cp-search-wrap { position: relative; display: flex; align-items: center; }
.cp-search-icon { position: absolute; left: 10px; color: var(--ink-3); pointer-events: none; }
.cp-search {
  height: 34px; padding: 0 10px 0 32px; border: 1px solid var(--line);
  border-radius: var(--r-ctrl); font-size: 13px; color: var(--ink);
  background: var(--surface); outline: none; width: 200px;
}
.cp-search:focus { border-color: var(--accent); }
.cp-filter-btn-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctrl);
  background: var(--surface);
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
  white-space: nowrap;
}
.cp-filter-btn-wrap:hover { border-color: var(--ink-3); }
.cp-filter-label { color: var(--ink-3); }
.cp-filter-value { font-weight: 500; color: var(--ink); }
.cp-filter-select {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
}

/* Grid */
.cp-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* Card */
.cp-card {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--r-card); overflow: hidden; cursor: pointer;
  transition: box-shadow .18s, transform .18s;
  display: flex; flex-direction: column;
}
.cp-card:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }

/* Cover image */
.cp-card-img {
  position: relative; height: 180px; background: var(--surface-3); overflow: hidden; flex-shrink: 0;
}
.cp-card-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cp-card-img-placeholder {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
}
.cp-card-badges { position: absolute; top: 10px; left: 10px; }
.cp-badge { border-radius: 20px; padding: 3px 8px; font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.cp-badge--type { background: rgba(0,0,0,.55); color: #fff; backdrop-filter: blur(4px); }
.cp-card-title-overlay {
  position: absolute; bottom: 0; left: 0; right: 0; padding: 32px 14px 12px;
  background: linear-gradient(transparent, rgba(0,0,0,.6));
  color: #fff; font-size: 15px; font-weight: 700; line-height: 1.3;
}

/* Card body */
.cp-card-body {
  padding: 14px 16px 16px;
  display: flex; flex-direction: column; gap: 10px;
  flex: 1;
}

/* Status badge + category */
.cp-card-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.cp-status-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px; border-radius: 20px;
  font-size: 12px; font-weight: 600;
}
.cp-status-dot-sm {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0;
}
.cp-status-badge.cp-status-dot--active  { background: #e8f7ee; color: #1e8a45; }
.cp-status-badge.cp-status-dot--warning { background: #fff4e0; color: #d48000; }
.cp-status-badge.cp-status-dot--draft   { background: #fff4e0; color: #c97a00; }
.cp-status-badge.cp-status-dot--ended   { background: #f3f3f3; color: #777; }
.cp-cat-badge {
  padding: 3px 9px; background: var(--surface-3);
  border-radius: 20px; font-size: 12px; color: var(--ink-3); font-weight: 500;
}

/* Description */
.cp-card-desc {
  font-size: 13px; color: var(--ink-2); margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* Info box */
.cp-info-box {
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  overflow: hidden;
}
.cp-info-row {
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
  background: #faf8f6;
}
.cp-info-label {
  display: block; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .06em; color: var(--ink-3); margin-bottom: 3px;
}
.cp-info-val { font-size: 13px; color: var(--ink-2); }

/* Stats grid */
.cp-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 4px 0;
  background: #faf8f6;
}
.cp-stat-item {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px;
  color: var(--ink-3);
}
.cp-stat-item svg { margin-top: 2px; flex-shrink: 0; }
.cp-stat-label { display: block; font-size: 11px; font-weight: 500; color: var(--ink-3); }
.cp-stat-val   { display: block; font-size: 13px; font-weight: 600; color: var(--ink); }

/* Progress */
.cp-progress-wrap { display: flex; align-items: center; gap: 8px; }
.cp-progress-bar { flex: 1; height: 4px; background: var(--line); border-radius: 2px; overflow: hidden; }
.cp-progress-fill { height: 100%; background: var(--accent); border-radius: 2px; transition: width .3s; }
.cp-progress-pct { font-size: 11px; font-weight: 600; color: var(--ink-3); white-space: nowrap; }

/* CTA */
.cp-cta-row { display: flex; gap: 8px; margin-top: auto; }
.cp-cta {
  flex: 1; height: 38px; background: var(--accent); color: #fff;
  border-radius: var(--r-ctrl); font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background .15s; border: none;
}
.cp-cta:hover { background: var(--accent-strong); }
.cp-cta--draft { background: var(--ink); }
.cp-cta--draft:hover { background: var(--ink-2); }
.cp-cta-delete {
  width: 38px; height: 38px; flex-shrink: 0;
  border: 1px solid var(--line); border-radius: var(--r-ctrl);
  background: var(--surface); color: var(--ink-3); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .15s, color .15s;
}
.cp-cta-delete:hover { border-color: #e53935; color: #e53935; }

/* Empty */
.cp-empty {
  text-align: center; padding: 64px 20px;
  display: flex; flex-direction: column; align-items: center;
}

/* Pagination */
.cp-pagination { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 32px; }
.cp-page-btn {
  width: 32px; height: 32px; border: 1px solid var(--line); border-radius: var(--r-ctrl);
  background: var(--surface); font-size: 15px; cursor: pointer; color: var(--ink);
}
.cp-page-btn:disabled { opacity: .35; cursor: default; }

/* Skeleton */
.cp-skeleton {
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-card); overflow: hidden;
}
.cp-skel { background: var(--line-2); border-radius: 4px; animation: shimmer 1.4s infinite; }
.cp-skel--img { height: 180px; border-radius: 0; }
.cp-skel--line { height: 12px; }


.cp-dialog-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.4);
  display: flex; align-items: center; justify-content: center; z-index: 999;
}
.cp-dialog {
  background: var(--surface); border-radius: var(--r-card);
  padding: 28px; width: 100%; max-width: 400px; box-shadow: var(--shadow-lg);
}
.cp-dialog-title { font-size: 17px; font-weight: 700; margin: 0 0 4px; color: var(--ink); }
.cp-dialog-sub   { font-size: 13px; color: var(--ink-3); margin: 0 0 16px; }
.cp-dialog-select {
  width: 100%; height: 40px; padding: 0 12px;
  border: 1px solid var(--line); border-radius: var(--r-ctrl);
  font-size: 13px; background: var(--surface); color: var(--ink);
  margin-bottom: 20px; outline: none;
}
.cp-dialog-actions { display: flex; justify-content: flex-end; gap: 10px; }
.cp-dialog-cancel {
  height: 38px; padding: 0 16px; border: 1px solid var(--line);
  border-radius: var(--r-ctrl); background: var(--surface);
  font-size: 13px; cursor: pointer; color: var(--ink);
}

@keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:.45} }

/* Responsive */
@media (max-width: 960px) { .cp-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 600px) {
  .cp-grid { grid-template-columns: 1fr; }
  .cp-filters { flex-direction: column; align-items: flex-start; }
  .cp-search { width: 100%; }
}
</style>